import { extractMoneyCandidates } from "../money-parser.js";
import { scrubPiiForLlm } from "../pii-scrubber.js";
import { normalizeViText } from "../vi-normalizer.js";
import type { AiSlotType } from "../registry/schema.js";
import type { InterpreterSpanLike } from "./output.js";

/**
 * 🔴 US-AIV2-0110 S19 (@UC0267) — LƯỚI ĐỠ TẤT ĐỊNH CHO Ô TIỀN.
 *
 * ## Vấn đề (đo trên bản ghi eval thật, 26/09)
 * 9 câu ở 3 trang tiền (INC-RECORD · EXP-F-NOALLOC · EXP-F-EQUAL): model gạch **CẢ CÂU** vào ô chữ (`nguon`/`noiDung`) và **không
 * gạch** ô `soTien` — *"ghi tai tro 2tr"* ⇒ `spans: [{nguon: "ghi tai tro 2tr"}]`. Ô tiền trống ⇒ trợ lý hỏi *"Thu về bao nhiêu?"*
 * dù người dùng vừa nói 2tr. Lặp lại ở lượt đo thứ hai ⇒ KHÔNG phải nhiễu; sửa prompt thì lật trang khác (§3b D).
 *
 * ## Luật (MỘT hàm, hai người gọi — Người làm và bộ chấm eval)
 * Điền ô tiền bằng số đọc từ **CẢ CÂU** khi và chỉ khi đủ SÁU điều:
 *  0. 🔴 (S19b) trang **KHAI trong Sổ** `moneyFromSentence: "sole-amount"` — lời khai rằng ô tiền là SỐ TIỀN DUY NHẤT của giao
 *     dịch. **Trang chưa khai ⇒ KHÔNG BAO GIỜ áp.** Đây là chốt AN TOÀN CHÍNH (liệt kê cái được PHÉP; thiếu ⇒ hỏi). Rà đối kháng
 *     S19 (CRITICAL) đo được bản không-khai điền *"mở đợt thu tổng 3 triệu cho 30 bạn"* thành 3.000.000 MỖI NGƯỜI (COL-EQUAL),
 *     *"chia đều cho 5 người, mỗi người 200k"* thành TỔNG 200.000 (EXP-F-EQUAL), *"ghi nhận Minh đóng rồi, còn thiếu 50k"* thành
 *     ĐÃ ĐÓNG 50.000 (RECORD-PAID) — lưới không biết NGHĨA ô, và chỉ Sổ biết. Hôm nay bật: INC-RECORD · EXP-F-NOALLOC;
 *  1. trang có **ĐÚNG MỘT** ô `type: "money"` (0 ô ⇒ không có gì để điền; ≥2 ô ⇒ không biết số thuộc ô nào) — validator Sổ cũng
 *     gác vế này lúc khai; ở đây kiểm lại lúc chạy (hai chốt độc lập);
 *  2. Phiếu **ĐÃ NEO** không có span nào cho ô đó — kể cả span đọc không ra số: model đã gạch thì S17 hỏi lại CÓ LÝ DO như cũ,
 *     lưới **không bao giờ đè** một span sẵn có (đè = chọn con số mình thích hơn chữ model gạch);
 *  3. câu sau khi **cạo PII** (`scrubPiiForLlm` — CÙNG câu mà LLM thấy, idempotent) còn chữ;
 *  4. bộ đọc tiền DUY NHẤT `extractMoneyCandidates` trả `resolved` — tức ĐÚNG MỘT giá trị, sau MỌI luật an toàn đã có
 *     ("2 trăm" ⇒ hỏi · đuôi ghép "1tr2" · rưỡi · năm · số của kỳ · danh từ đếm · số trần <4 chữ số · cụm chữ chưa đọc · ≥2 số).
 *     `ambiguous`/`none` ⇒ `null` ⇒ hỏi như cũ. Không có luật đọc thứ hai ở đây (CLAUDE.md §3.8);
 *  5. (S19b, lớp PHỤ) câu KHÔNG mang dấu hiệu quan hệ/phân bổ (`STRUCTURAL_VETO_RE` + `VETO_PHRASES`: mỗi · /người · "20k một bạn" · đầu người ·
 *     còn thiếu/nợ/dư/lại · trong tổng · tổng cộng · chia) — kể cả trên trang đã bật, một số trong câu kiểu đó có thể là một
 *     PHẦN chứ không phải cả giao dịch. Lớp này KHÔNG phải an toàn chính: nó là danh sách kích hoạt, và danh sách kích hoạt thì
 *     lọt (bài học S18). An toàn chính là vế 0; vế 5 chỉ chặn thêm những câu lạ trên trang đã bật.
 *  6. (S19c, lớp PHỤ) câu KHÔNG mang dấu hiệu THAM CHIẾU / KẾ HOẠCH / SỐ DƯ (`VETO_PHRASES` + `DEPOSIT_PHRASES`, so theo DẤU từng từ — S19d): tiền gốc ·
 *     số dư · quỹ còn/hiện có/tồn quỹ/còn trong quỹ · ngân sách · hạn mức · định mức · niêm yết/giá bìa/giá gốc/giá đề xuất/báo
 *     giá · dự kiến/dự tính/dự trù/ước tính/kế hoạch · phê duyệt · cam kết/hứa · tạm ứng · khoảng/tầm/gần/hơn/ngót/chừng/tối
 *     đa/tối thiểu/trên/dưới + số · đặt cọc/tiền cọc TRỪ khi câu nói HOÀN/trả lại/nhận lại cọc (hoàn cọc LÀ khoản thu —
 *     *"được hoàn 300k tiền đặt cọc"* là câu thật của INC-RECORD). Một số trong câu kiểu đó là số THAM CHIẾU, không phải số của
 *     giao dịch: *"lãi tháng này, gốc 50 triệu"* không phải thu 50 triệu (rà vòng 2 S19b đo 11/11 câu điền sai trước lớp này).
 *
 * ## 🔴 QĐ NGƯỜI DÙNG (PO) 26/09/2026 — ĐỌC TRƯỚC KHI "SỬA LẠI CHO AN TOÀN HƠN"
 * Sau rà vòng 2 S19b (HIGH: phủ quyết chỉ bắt dấu hiệu phân bổ, chưa bắt tham chiếu/kế hoạch ⇒ 11/11 câu điền sai), PO chọn
 * **"Tự điền + danh sách phủ quyết dày hơn"**. Hai phương án đã cân và **KHÔNG chọn**:
 *   · **"Gợi ý 1 nút, không tự điền"** — lưới chỉ đề xuất con số thành một nút cho người dùng bấm, không điền ô. An toàn hơn,
 *     nhưng thêm một lượt bấm cho đúng những câu mà người dùng đã nói rõ số.
 *   · **"Bỏ lưới"** — quay về hỏi "bao nhiêu" mỗi khi model không gạch ô tiền (hành vi trước S19).
 * **Rủi ro CHẤP NHẬN có chủ đích:** cách nói tham chiếu CHƯA liệt kê trong phủ quyết có thể bị điền sai (danh sách kích hoạt
 * thì lọt — đúng bài học S18). **Chốt cuối là phiếu xác nhận hiện số tiền TRƯỚC khi bấm Đồng ý** (T1/T2), và sửa sau ghi sổ là
 * REVERSAL (CLAUDE.md §3.3). Muốn đổi quyết định này (sang gợi-ý-nút hay bỏ lưới) ⇒ hỏi PO, không tự đổi.
 *
 * ## Vì sao an toàn (AI-INV-2)
 * Con số đến từ **chữ người dùng** qua đường tất định — LLM không tính, không viết số nào. Giá trị điền đi tiếp dưới dạng CHỮ
 * (`text` = cả câu đã cạo PII) vào đúng resolver ô tiền (`resolveMoneySlot` ⇒ `extractMoneyCandidates` lần nữa) ⇒ cùng một con
 * số, và phiếu xác nhận vẫn in số đó TRƯỚC khi người dùng bấm Đồng ý (T1/T2). Phiếu của LLM (`ai_audit_log`, append-only)
 * **không bị sửa**: lưới không chèn span nào vào Phiếu — nó chỉ đưa thêm một ô chữ cho resolver của Người làm, nên bản ghi vẫn
 * nói đúng điều model đã nói.
 *
 * ## Vì sao cạo PII trước khi đọc
 * Số tài khoản / điện thoại là chuỗi chữ số dài ⇒ bộ đọc coi là số trần ≥4 chữ số ⇒ tiền. *"ghi thu tài trợ, stk 0123456789"*
 * mà không cạo ⇒ điền 123.456.789 đ lặng lẽ. Cạo xong ⇒ `[STK]` ⇒ không có số ⇒ hỏi. Hệ quả chấp nhận: số tiền viết liền ≥8 chữ
 * số (*"20000000"*) cũng bị cạo như STK ⇒ hỏi — đúng như LLM (nó cũng chỉ thấy `[STK]`), chiều an toàn.
 *
 * ## Ô tiền TUỲ CHỌN (RECORD-PAID · PAY-REPORT: vắng = "đúng số còn phải đóng")
 * (S19b) KHÔNG bật: ô là PHẦN ĐÃ TRẢ, và câu người dùng hay nói kèm phần CÒN THIẾU (*"đóng rồi, còn thiếu 50k"*) ⇒ số duy nhất
 * trong câu có thể là số ngược nghĩa. Bản S19 đầu áp như ô bắt buộc — rà đối kháng bác. Ô vắng ⇒ mặc định "đủ" giữ nguyên.
 *
 * ## Giới hạn đã biết (KHÔNG làm ở đây)
 *  - Ô chữ (`nguon`/`noiDung`) vẫn mang CẢ CÂU gồm cụm tiền (*"ghi tai tro 2tr"*) — phiếu hiện nguồn "ghi tai tro 2tr" và số
 *    tiền 2.000.000 đ. Sửa ô chữ là việc của S20 (ở gốc prompt), không cắt chữ hộ model ở đây.
 *  - Chỉ áp lúc MỞ việc từ một câu (Người làm `startPage`) — đúng chỗ bộ chấm eval đo. Giữa form, luật "ô đang hỏi nhận cả câu"
 *    (`answerSlot`) đã có sẵn; không áp lưới ở đó để không đè một ô tiền ĐÃ điền ở lượt trước.
 *  - Span model gạch cho ô tiền mà bị lớp neo VỨT (chữ không có trong câu) thì Phiếu ĐÃ NEO không còn span đó ⇒ lưới đọc câu.
 *    Cố ý: số model tự viết đã bị vứt, số điền là số trong câu. Người làm chỉ thấy Phiếu đã neo, nên eval cũng phải xét trên đó.
 */
export interface SoleMoneySlotFill {
  /** Id ô tiền DUY NHẤT của trang. */
  readonly slot: string;
  /** Chữ đưa cho resolver ô tiền — CẢ CÂU đã cạo PII, đã `trim`. Resolver đọc lại ra đúng `amountMinor`. */
  readonly text: string;
  /** Số đọc được — BigInt minor units (CLAUDE.md §3.1). */
  readonly amountMinor: bigint;
}

/** Hình ô TỐI THIỂU cần — `AiSlot` của Sổ thoả, không kéo cả schema. */
export interface MoneyFallbackSlotLike {
  readonly id: string;
  readonly type: AiSlotType;
}

/** Hình trang TỐI THIỂU — `AiCapabilityPage` của Sổ thoả. `moneyFromSentence` vắng ⇒ lưới không áp. */
export interface MoneyFallbackPageLike {
  readonly slots: readonly MoneyFallbackSlotLike[];
  readonly moneyFromSentence?: "sole-amount";
}

/**
 * S19b vế 5 — phần CẤU TRÚC (dính số / dấu `/`): so trên câu đã cạo dấu. "một/1 + người/bạn/suất…" chỉ tính khi đứng NGAY SAU
 * số/đơn vị tiền ("20k một bạn") hoặc NGAY TRƯỚC số ("một bạn 200k"): *"công ty mẹ của một bạn tài trợ 10 triệu"*
 * (INC-RECORD#38, câu thật) là người tài trợ, không phải phân bổ. Phần TỪ VỰNG của vế 5 nằm ở `VETO_PHRASES` (so theo dấu).
 */
const PER_UNIT_NOUN = "(?:nguoi|ban|suat|dau(?: nguoi)?|thanh vien|em|chau|be|hoc sinh|phu huynh|nha|ho|lan|thang)";
const STRUCTURAL_VETO_RE = new RegExp(
  [
    `/\\s*${PER_UNIT_NOUN}(?![a-z])`,
    `(?:\\d|(?:^|[^a-z])(?:k|tr|trieu|nghin|ngan|cu|dong|d|ty|ti))\\s*(?:mot|1)\\s+${PER_UNIT_NOUN}(?![a-z])`,
    `(?:^|[^a-z])(?:mot|1)\\s+${PER_UNIT_NOUN}\\s*\\d`,
  ].join("|"),
  "u",
);

/**
 * 🔴 S19d (rà vòng 3) — PHỦ QUYẾT TỪ VỰNG SO THEO DẤU CỦA TỪNG TỪ.
 *
 * Bản S19b/S19c so trên câu ĐÃ CẠO DẤU ⇒ phủ quyết OAN câu bình thường có dấu: *"chú Tâm ủng hộ 2 triệu"* ("tam ung" ≡ "tạm
 * ứng" — Tâm là tên rất phổ biến, "ủng hộ" là động từ chính của INC-RECORD) · *"hơn nữa"* ≡ "hơn nửa" · *"góc phố"* ≡ "gốc" ·
 * họ "Hứa" ≡ "hứa" · "mới" ≡ "mỗi". Luật mới, xét TỪNG TỪ của câu (tách theo chữ/số, chữ thường, NFC):
 *   · từ gõ CÓ dấu ⇒ phải trùng ĐÚNG mẫu viết có dấu ("tâm" ≠ "tạm", "góc" ≠ "gốc", "nữa" ≠ "nửa");
 *   · từ gõ KHÔNG dấu ⇒ khớp mẫu đã cạo dấu ("tam ung" ⇒ phủ quyết — không biết người gõ muốn "tạm ứng" hay "Tâm ủng", chọn
 *     hướng AN TOÀN: hỏi thừa; câu không dấu giữ nguyên độ phủ quyết của S19c).
 * Câu TRỘN (*"chi tien sửa quạt, ngan sach 500k"*) tự đúng vì luật xét từng từ: "ngan sach" gõ không dấu ⇒ khớp; *"ngân sach"*
 * (một từ có dấu đúng + một từ không dấu) ⇒ khớp. Không có ngưỡng "câu có dấu hay không" cho cả câu — một câu có vài dấu
 * không làm các từ gõ trơn của nó thôi bị xét.
 * Họ "Hứa": từ "hứa" VIẾT HOA mà KHÔNG đứng đầu câu ⇒ coi là tên riêng (bỏ qua); các từ khác không có ngoại lệ tên riêng.
 *
 * 🔴 S19e (rà vòng 4) — ĐẢO LUẬT SANG "LIỆT KÊ CÁI ĐƯỢC THA". Luật hai nhánh ở trên ("đúng dấu" / "không dấu") để một KHE:
 * từ gõ CÓ dấu mà SAI MỘT dấu (*"quĩ còn 5 triệu"* — ĩ vs ỹ · *"gôc"* · *"niêm yêt"* · *"dư kiến"*) không thuộc nhánh nào ⇒ lọt.
 * Luật hiện hành (`slotMatches`): từ khớp ĐÚNG dấu mẫu ⇒ khớp; không thì mặt chữ CẠO DẤU (coi `i≡y`: quĩ/quỹ, kĩ/kỹ) trùng mẫu
 * ⇒ khớp, **TRỪ KHI** từ (có dấu, NFC, chữ thường) nằm NGUYÊN VĂN trong tập ĐÓNG `HARMLESS_LOOKALIKES` — những từ vô hại ĐÃ
 * ĐO được là trùng mặt chữ với một từ phủ quyết (tâm ≈ tạm · góc ≈ gốc · nữa ≈ nửa · mới ≈ mỗi). Họ "Hứa" giữ luật viết hoa.
 * ⚠️ Thiếu một từ vô hại trong tập ⇒ câu bị phủ quyết THỪA ⇒ trợ lý HỎI số tiền (an toàn). ĐỪNG đảo lại thành "liệt kê cái bị
 * phủ quyết theo đúng dấu": đó chính là khe vừa vá. Thêm từ vào tập tha chỉ khi đo được câu thật bị hỏi oan.
 */
type VetoToken = { readonly orig: string; readonly capitalized: boolean };
/** Một ô của mẫu: danh sách từ VIẾT CÓ DẤU được nhận; `#NUM` = token có chữ số ("300k", "2"); `=x` = CHỈ nhận đúng dấu `x`. */
type VetoSlot = readonly string[];

const NUMBER_WORDS = ["một", "hai", "ba", "bốn", "tư", "năm", "sáu", "bảy", "tám", "chín", "mười", "mươi", "trăm", "vài", "mấy"];
const RANK_WORDS = ["triệu", "tr", "nghìn", "ngàn", "k", "củ", "tỷ", "tỉ", "trăm"];
const APPROX_WORDS = ["khoảng", "gần", "hơn", "ngót", "chừng", "trên", "dưới"];
const APPROX_PREFIXES: readonly VetoSlot[][] = [[APPROX_WORDS], [["xấp"], ["xỉ"]], [["tối"], ["đa", "thiểu"]]];

/** Mọi mẫu TỪ VỰNG (vế 5 + vế 6). Mỗi mẫu là một dãy ô, khớp liên tiếp theo từ. */
const VETO_PHRASES: readonly (readonly VetoSlot[])[] = [
  // vế 5 — phân bổ / số dư
  [["mỗi"]],
  [["đầu"], ["người"]],
  [["còn"], ["thiếu", "nợ", "dư", "lại", "phải"]],
  [["trong"], ["tổng"]],
  [["tổng"], ["cộng"]],
  [["chia"]],
  // vế 6 — tham chiếu / kế hoạch / số dư (S19c)
  [["gốc"]],
  [["số"], ["dư"]],
  [["quỹ"], ["còn"]],
  [["hiện"], ["có"]],
  [["còn"], ["trong"], ["quỹ"]],
  [["tồn"], ["quỹ"]],
  [["ngân"], ["sách"]],
  [["hạn", "định"], ["mức"]],
  [["niêm"], ["yết"]],
  [["giá"], ["bìa"]],
  [["giá"], ["đề"], ["xuất"]],
  [["báo"], ["giá"]],
  [["dự"], ["kiến", "tính", "trù"]],
  [["ước"], ["tính"]],
  [["kế"], ["hoạch"]],
  [["phê"], ["duyệt"]],
  [["cam"], ["kết"]],
  [["hứa"]],
  [["tạm"], ["ứng"]],
  // xấp xỉ + số: chữ số · số bằng chữ · "nửa" CHỈ trước bậc tiền ("hơn nửa triệu" — "hơn nữa" là liên từ, S19d)
  ...APPROX_PREFIXES.flatMap((pre) => [
    [...pre, ["#NUM"]],
    [...pre, NUMBER_WORDS],
    [...pre, ["nửa"], RANK_WORDS],
  ]),
  // "tầm": gõ KHÔNG dấu trùng "tám" (số 8) ⇒ chỉ tính trước CHỮ SỐ; gõ ĐÚNG "tầm" ⇒ tính cả trước số bằng chữ (S19d).
  [["tầm"], ["#NUM"]],
  [["=tầm"], NUMBER_WORDS],
  [["=tầm"], ["nửa"], RANK_WORDS],
];
/** Cọc: "đặt cọc 500k" là tiền ĐEM ĐI giữ chỗ — tham chiếu; "được HOÀN/trả lại/nhận lại/lấy lại cọc" là khoản THU thật (INC-RECORD). */
const DEPOSIT_PHRASES: readonly (readonly VetoSlot[])[] = [[["cọc"]]];
const DEPOSIT_RETURNED_PHRASES: readonly (readonly VetoSlot[])[] = [[["hoàn"]], [["trả", "nhận", "lấy"], ["lại"]]];

/** Tập ĐÓNG từ vô hại trùng mặt chữ (cạo dấu) với một từ phủ quyết — đo ở rà vòng 3/4 S19. Chỉ THÊM khi có câu thật bị hỏi oan. */
const HARMLESS_LOOKALIKES: ReadonlySet<string> = new Set(["tâm", "góc", "nữa", "mới"]);
/** Mặt chữ để so: cạo dấu + chữ thường + `y` ≡ `i` (quĩ/quỹ, kĩ/kỹ, mĩ/mỹ — hai cách viết đều gặp). */
const lookalikeKey = (w: string): string => normalizeViText(w).replace(/y/gu, "i");

function tokenize(sentence: string): VetoToken[] {
  const nfc = sentence.normalize("NFC");
  const out: VetoToken[] = [];
  for (const m of nfc.matchAll(/[\p{L}\p{N}]+/gu)) {
    const raw = m[0];
    const orig = raw.toLowerCase();
    out.push({ orig, capitalized: raw !== orig && raw[0] !== orig[0] });
  }
  return out;
}

function slotMatches(tok: VetoToken, slot: VetoSlot, index: number): boolean {
  return slot.some((want) => {
    if (want === "#NUM") return /\d/u.test(tok.orig);
    if (want.startsWith("=")) return tok.orig === want.slice(1);
    // Họ "Hứa" viết hoa giữa câu ⇒ tên riêng, không phải "hứa".
    if (want === "hứa" && tok.capitalized && index > 0) return false;
    if (tok.orig === want) return true;
    if (HARMLESS_LOOKALIKES.has(tok.orig)) return false;
    return lookalikeKey(tok.orig) === lookalikeKey(want);
  });
}

function containsPhrase(tokens: readonly VetoToken[], phrases: readonly (readonly VetoSlot[])[]): boolean {
  for (const phrase of phrases) {
    for (let i = 0; i + phrase.length <= tokens.length; i += 1) {
      if (phrase.every((slot, k) => slotMatches(tokens[i + k]!, slot, i + k))) return true;
    }
  }
  return false;
}

/** Vế 5 + vế 6: câu nói về một PHẦN / QUAN HỆ / số THAM CHIẾU chứ không phải cả giao dịch ⇒ không điền. */
function isVetoedSentence(text: string): boolean {
  if (STRUCTURAL_VETO_RE.test(normalizeViText(text))) return true;
  const tokens = tokenize(text);
  if (containsPhrase(tokens, VETO_PHRASES)) return true;
  return containsPhrase(tokens, DEPOSIT_PHRASES) && !containsPhrase(tokens, DEPOSIT_RETURNED_PHRASES);
}

/**
 * @param sentence câu người dùng (thô hoặc đã cạo — hàm tự cạo, idempotent).
 * @param anchoredSpans span của Phiếu **ĐÃ NEO** (`anchorSpansToSentence(...).spans` / `envelope.spans` sau cổng phiên dịch).
 * @param page trang Phiếu chọn (Sổ); `null` ⇒ chưa biết trang ⇒ không áp. Trang không khai `moneyFromSentence` ⇒ không áp.
 */
export function fillSoleMoneySlotFromSentence(
  sentence: string,
  anchoredSpans: readonly InterpreterSpanLike[],
  page: MoneyFallbackPageLike | null,
): SoleMoneySlotFill | null {
  if (page === null || page.moneyFromSentence !== "sole-amount") return null;
  const money = page.slots.filter((s) => s.type === "money");
  if (money.length !== 1) return null;
  const slot = money[0]!.id;
  if (anchoredSpans.some((s) => s.slot === slot)) return null;
  const text = scrubPiiForLlm(sentence).text.trim();
  if (text === "") return null;
  if (isVetoedSentence(text)) return null;
  const read = extractMoneyCandidates(text);
  if (read.kind !== "resolved") return null;
  return { slot, text, amountMinor: read.amountMinor };
}
